import { AlertCircle, CheckCircle2, Info, LoaderCircle } from "lucide-react";
import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from "react";
import type { EffectiveRoleCode } from "../api/models";
import { revealField } from "../lib/reveal-field";
import { reviewContentForDisplay } from "../lib/review-display";
import { reviewTextForDisplay } from "../lib/review-text";
import { cn } from "../lib/utils";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import { Badge } from "./ui/badge";
import { Button as CyraButton } from "./ui/button";
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyTitle } from "./ui/empty";
import { Field as CyraField, FieldDescription, FieldError, FieldLabel } from "./ui/field";
import { Input as CyraInput } from "./ui/input";
import { NativeSelect } from "./ui/native-select";
import { Textarea as CyraTextarea } from "./ui/textarea";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  size?: "default" | "small";
  variant?: ButtonVariant;
}

const buttonVariant = {
  danger: "destructive",
  ghost: "ghost",
  primary: "default",
  secondary: "outline",
} as const;

export function Button({
  children,
  size = "default",
  type = "button",
  variant = "secondary",
  className,
  ...props
}: ButtonProps) {
  return (
    <CyraButton
      {...props}
      className={cn(
        "h-auto min-h-8 px-2.5 py-1.5 leading-snug transition-colors",
        size === "small" && "min-h-7 px-2 py-1",
        className,
      )}
      data-action-priority={variant === "primary" || variant === "danger" ? "primary" : "secondary"}
      size={size === "small" ? "sm" : "default"}
      type={type}
      variant={buttonVariant[variant]}
    >
      {reviewContentForDisplay(children)}
    </CyraButton>
  );
}

interface FieldProps {
  children: ReactNode;
  error?: string | undefined;
  htmlFor: string;
  label: string;
  optional?: boolean;
}

export function Field({ children, error, htmlFor, label, optional = false }: FieldProps) {
  return (
    <CyraField className="min-w-0 gap-1.5" data-invalid={Boolean(error)}>
      <FieldLabel htmlFor={htmlFor}>
        {reviewTextForDisplay(label)}
        {optional ? <FieldDescription>Optional</FieldDescription> : null}
      </FieldLabel>
      {children}
      {error ? (
        <FieldError id={`${htmlFor}-error`}>{reviewTextForDisplay(error)}</FieldError>
      ) : null}
    </CyraField>
  );
}

export function Input(props: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <CyraInput
      {...props}
      className={cn("h-8", props.className)}
      onInvalid={(event) => {
        revealField(event.currentTarget, false);
        props.onInvalid?.(event);
      }}
    />
  );
}

export function Select(props: Omit<SelectHTMLAttributes<HTMLSelectElement>, "size">) {
  return (
    <NativeSelect
      {...props}
      size="sm"
      className={props.className}
      onInvalid={(event) => {
        revealField(event.currentTarget, false);
        props.onInvalid?.(event);
      }}
    />
  );
}

export function Textarea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <CyraTextarea
      {...props}
      className={cn("min-h-16 max-h-48 resize-y", props.className)}
      onInvalid={(event) => {
        revealField(event.currentTarget, false);
        props.onInvalid?.(event);
      }}
    />
  );
}

const successStates = new Set([
  "ALLOWED",
  "ACTIVE",
  "READY",
  "SUCCEEDED",
  "VERIFIED",
  "CURRENT",
  "ACCEPTED",
  "APPROVED",
  "COMPLETE",
  "SENT",
]);
const warningStates = new Set([
  "UNVERIFIED",
  "PENDING",
  "PROCESSING",
  "SUSPENDED",
  "DRAFT",
  "INVITED",
  "SCHEDULED",
  "PROVISIONING",
  "DELETION_PENDING",
  "HELD",
  "NEEDS_RETRY",
]);
const dangerStates = new Set([
  "BANNED",
  "FAILED",
  "DENIED",
  "CANCELLED",
  "EXPIRED",
  "REJECTED",
  "FAILED_TERMINAL",
]);

const badgeTone = {
  danger: "bg-rose-500",
  neutral: "bg-muted-foreground/50",
  success: "bg-emerald-600",
  warning: "bg-amber-500",
} as const;

export function stateLabel(value: string): string {
  const words = value.replaceAll("_", " ").toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function StatusBadge({ value }: { value: string }) {
  const tone = successStates.has(value)
    ? "success"
    : warningStates.has(value)
      ? "warning"
      : dangerStates.has(value)
        ? "danger"
        : "neutral";
  return (
    <span
      className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs text-muted-foreground"
      data-state={value}
    >
      <span aria-hidden="true" className={`size-1.5 shrink-0 rounded-full ${badgeTone[tone]}`} />
      {reviewTextForDisplay(stateLabel(value))}
    </span>
  );
}

export const roleLabel: Record<EffectiveRoleCode, string> = {
  ADMIN: "Administrator",
  CONTENT_EDITOR: "Content editor",
  MODERATOR: "Moderator",
  PARTNER_CONTENT_MANAGER: "Content manager",
  USER: "User",
};

export function RoleBadge({ role }: { role: EffectiveRoleCode }) {
  return (
    <Badge className="border-0 bg-muted/50 font-normal text-muted-foreground" variant="outline">
      {roleLabel[role]}
    </Badge>
  );
}

interface NoticeProps {
  children: ReactNode;
  id?: string;
  title: string;
  tone?: "info" | "success" | "warning" | "danger";
}

const noticeTone = {
  danger: "border-destructive/25 bg-destructive/5 text-foreground",
  info: "border-0 bg-transparent px-0 py-2 text-muted-foreground",
  success: "border-0 bg-transparent px-0 py-2 text-muted-foreground",
  warning: "border-amber-500/25 bg-amber-500/5 text-foreground",
} as const;

export function Notice({ children, id, title, tone = "info" }: NoticeProps) {
  const Icon = tone === "success" ? CheckCircle2 : tone === "danger" ? AlertCircle : Info;
  return (
    <Alert className={noticeTone[tone]} id={id} role={tone === "danger" ? "alert" : "note"}>
      <Icon aria-hidden="true" />
      <AlertTitle>{reviewTextForDisplay(title)}</AlertTitle>
      <AlertDescription className="text-current">
        {reviewContentForDisplay(children)}
      </AlertDescription>
    </Alert>
  );
}

interface StatePanelProps {
  action?: ReactNode;
  description?: string;
  kind?: "empty" | "error" | "loading";
  requestId?: string | undefined;
  title: string;
}

export function StatePanel({
  action,
  description,
  kind = "empty",
  requestId,
  title,
}: StatePanelProps) {
  return (
    <Empty
      className="min-h-28 border-0 bg-transparent py-4"
      role={kind === "error" ? "alert" : "status"}
    >
      <EmptyHeader>
        {kind === "loading" ? (
          <LoaderCircle aria-hidden="true" className="size-4 animate-spin text-muted-foreground" />
        ) : null}
        <EmptyTitle>{reviewTextForDisplay(title)}</EmptyTitle>
        {description ? (
          <EmptyDescription>{reviewTextForDisplay(description)}</EmptyDescription>
        ) : null}
      </EmptyHeader>
      {action || requestId ? (
        <EmptyContent>
          {action}
          {requestId ? (
            <details className="text-xs text-muted-foreground">
              <summary className="cursor-pointer py-2">Support details</summary>
              <p className="break-all font-mono">Request ID · {requestId}</p>
            </details>
          ) : null}
        </EmptyContent>
      ) : null}
    </Empty>
  );
}

export function formatDate(value: number | null, timeZone?: string): string {
  if (value === null) return "Not recorded";
  if (!Number.isFinite(value)) return "Date unavailable";
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZoneName: "short",
    ...(timeZone ? { timeZone } : {}),
  }).format(value);
}

export function initials(firstName: string, lastName: string): string {
  return `${firstName[0] ?? ""}${lastName[0] ?? ""}`.toUpperCase();
}
